/**
 * Provides the `isEmpty` utility function for checking if a variable is considered "empty" based on specific criteria, and its associated `IsEmptyOptions` type.
 * @module isEmpty
 */
// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
/**
 * Checks if a variable is considered "empty" based on specific criteria.
 *
 * **Behavior:**
 * Determines emptiness based on the following rules (checked in order):
 * 1. **`null` or `undefined`**: Considered empty. Uses the `isDefined` utility internally.
 * 2. **Boolean `false`**: Considered empty.
 * 3. **Empty String (`''`)**: Considered empty.
 * 4. **Empty Array (`[]`)**: Considered empty.
 * 5. **Object with No Own Enumerable Keys**: Objects for which `Object.keys(obj).length` is `0` are considered empty. This includes plain empty objects (`{}`), but also instances of classes like `Date`, `RegExp`, `Map`, `Set`, and `Error` if they don't have custom enumerable properties added.
 *
 * **Non-empty values:**
 * All other values are considered **not empty**, including:
 * - Non-empty strings (including strings containing only whitespace).
 * - Non-empty arrays (even if they contain `null` or `undefined`).
 * - Objects with at least one own enumerable string key.
 * - Numbers (including `0`, `NaN`, `Infinity`).
 * - Boolean `true`.
 * - Functions.
 * - Symbols.
 * - BigInts.
 *
 * **Summary table:**
 *
 * | Input value (`variable`) | `isEmpty(variable)` Result |
 * | :----------------------- | :------------------------- |
 * | `null`                   | `true`                     |
 * | `undefined`              | `true`                     |
 * | `false`                  | `true`                     |
 * | `''`                     | `true`                     |
 * | `[]`                     | `true`                     |
 * | `{}`                     | `true`                     |
 * | `new Date()`             | `true`                     |
 * | `/regex/`                | `true`                     |
 * | `'hello'`                | `false`                    |
 * | `' '`                    | `false`                    |
 * | `[1]`                    | `false`                    |
 * | `[null]`                 | `false`                    |
 * | `{ a: 1 }`               | `false`                    |
 * | `0`                      | `false`                    |
 * | `NaN`                    | `false`                    |
 * | `true`                   | `false`                    |
 * | `()=>{}`                 | `false`                    |
 *
 * **Type predicates and type narrowing:**
 *
 * - **`isEmpty( variable: string | null | undefined ): variable is null | undefined | '';`**
 * If `isEmpty` is called with a variable that could be a string, null, or undefined, and the function returns `true`, TypeScript will narrow the type of `variable` to `null | undefined | ''` within the conditional block.
 * This means that, if `! isEmpty( variable )` is `true`, `variable` is narrowed to `string` (a non-empty string, as `''` makes `isEmpty` true).
 *
 * - **`isEmpty( variable: boolean | null | undefined ): variable is null | undefined | false;`**
 * If `isEmpty` is called with a variable that could be a `boolean`, `null`, or `undefined`, and it returns `true`, TypeScript narrows `variable` to `null | undefined | false`.
 * This means that, if `! isEmpty( variable )` is `true`, `variable` is narrowed to `true`.
 *
 * - **`isEmpty( variable: number | null | undefined ): variable is null | undefined;`**
 * If `isEmpty` is called with a variable that could be a `number`, `null`, or `undefined`, and it returns `true`,
 * TypeScript narrows `variable` to `null | undefined`. This is because numbers (including `0` and `NaN`) are *not* considered empty by this function.
 * This means that, if `! isEmpty( variable )` is `true`, `variable` is narrowed to `number`.
 *
 * - **`isEmpty( variable: symbol | null | undefined ): variable is null | undefined;`**
 * If `isEmpty` returns `true` for symbol-like inputs, `variable` is narrowed to `null | undefined`. (Symbols are not empty).
 * If `! isEmpty( variable )` is `true`, `variable` is narrowed to `symbol`.
 *
 * - **`isEmpty( variable: bigint | null | undefined ): variable is null | undefined;`**
 * If `isEmpty` returns `true` for bigint-like inputs, `variable` is narrowed to `null | undefined`. (BigInts are not empty).
 * If `! isEmpty( variable )` is `true`, `variable` is narrowed to `bigint`.
 *
 * - **`isEmpty<T>( variable: T[] | null | undefined ): variable is null | undefined | [];`**
 * For array inputs, if `isEmpty` returns `true`, TypeScript narrows `variable` to `null | undefined | []` (an empty array).
 * This means that, if `! isEmpty( variable )` is `true`, `variable` is narrowed to `T[]` (a non-empty array).
 *
 * - **`isEmpty<T extends object>( variable: T | null | undefined ): variable is null | undefined;`**
 * This overload is primarily for narrowing when `isEmpty` returns `true` *because* the input object was `null` or `undefined`.
 * If `isEmpty( obj )` returns `true` and `obj` was an actual object (e.g., `{}`), this specific predicate doesn't capture the "empty object" state for narrowing to a type like `{}`.
 * However, its main power is in the negated case: if `! isEmpty( obj )` is `true` (where `obj` was `T | null | undefined`), TypeScript can confidently narrow `obj` to `T` (a non-null, non-undefined object, which might still be an "empty" object like `{}` but is definitely not `null` or `undefined`), which means that we can use object properties without type errors.
 *
 * The final overload `( variable: unknown ): boolean;` is the general signature that the implementation matches.
 *
 * @since 3.3 Initial implementation.
 * @since 3.5 Converted into a utility function.
 *
 * @param {unknown} variable - The variable being evaluated. Can accept any type (`unknown`).
 * @return {boolean} `true` if the variable is considered empty according to the rules defined above, `false` otherwise.
 *
 * @example Importing the isEmpty utility
 * ```typescript
 * import { isEmpty } from '@automator/shared/utils/is-empty';
 * ```
 *
 * @example Checking standard empty values
 * Demonstrates the function returning true for common "empty" cases.
 * ```typescript
 * isEmpty(null);      // => true
 * isEmpty(undefined); // => true
 * isEmpty(false);     // => true
 * isEmpty('');        // => true
 * isEmpty([]);        // => true
 * isEmpty({});        // => true
 * ```
 *
 * @example Checking non-empty values
 * Demonstrates the function returning false for values not meeting the empty criteria.
 * ```typescript
 * isEmpty('hello');   // => false
 * isEmpty(' ');       // => false (whitespace string is not empty)
 * isEmpty([1]);       // => false
 * isEmpty({ a: 1 });  // => false
 * isEmpty(0);         // => false (number zero is not empty)
 * isEmpty(true);      // => false
 * isEmpty(NaN);       // => false
 * isEmpty(() => {});  // => false
 * ```
 *
 * @example Using with potentially undefined object property
 * Checks if an optional property exists and is not empty before processing.
 * ```typescript
 * interface User {
 *     name?: string | null;
 * }
 *
 * const user1: User = { name: 'Alice' };
 * const user2: User = { name: '' };
 * const user3: User = {};
 * const user4: User = { name: null };
 *
 * if ( ! isEmpty( user1.name ) ) {
 *     // TypeScript knows user1.name is 'string' here
 *     console.log( user1.name.toUpperCase() ); // Logs "ALICE"
 * }
 *
 * if ( ! isEmpty( user2.name ) ) {
 *     // Does not enter (name is '')
 * }
 *
 * if ( ! isEmpty( user3.name ) ) {
 *     // Does not enter (name is undefined)
 * }
 *
 * if ( ! isEmpty( user4.name ) ) {
 *     // Does not enter (name is null)
 * }
 * ```
 *
 * @remarks
 * - This function provides a specific definition of "empty" that includes `false`, empty strings/arrays/objects, `null`, and `undefined`. It differs from simple truthiness checks where `0` and `false` are considered falsy but not "empty" by this function.
 * - The check for empty objects relies on `Object.keys()`, which only considers own enumerable string properties. Objects with non-enumerable properties or symbol keys might be considered empty by this check if they lack enumerable string keys.
 * - For comprehensive behavior details, refer to the unit tests.
 */
export function isEmpty(variable) {
    // 1. Check for null or undefined using the isDefined type guard
    if (!isDefined(variable)) {
        return true;
    }
    // variable is now NonNullable<unknown>
    // 2. Check for the boolean value false
    // Need strict equality check
    if (variable === false) {
        return true;
    }
    // 3. Check for empty string
    if (typeof variable === 'string' && variable.length === 0) {
        return true;
    }
    // 4. Check for empty array
    if (Array.isArray(variable) && variable.length === 0) {
        return true;
    }
    // 5. Check for object with no own enumerable string properties
    if (typeof variable === 'object' && Object.keys(variable).length === 0) {
        return true;
    }
    // 6. If none of the above conditions for emptiness were met, it's not empty.
    return false;
}
;
